/**
 * หนังสือ HR — หน้ารายการฝั่ง HR (/hr/letters)
 *
 * ⭐ ปุ่ม "ออกจริง" ส่ง `expected_hash` ของฉบับที่จอกำลังแสดงไปด้วยเสมอ
 *    ถ้าไม่ตรง (มีคนแก้ / ทะเบียนขยับ) server ตอบ 409 พร้อมบอกว่าอะไรเปลี่ยน
 *    → จอแสดงรายการที่ต่างให้อ่าน แล้วให้กด "ดึงข้อมูลใหม่" ก่อน ไม่ออกเอกสารให้เงียบ ๆ
 *    (เอกสารมีคนเซ็นชื่อรับผิดชอบท้ายกระดาษ คนเซ็นต้องรับผิดชอบสิ่งที่เขาอ่าน)
 *
 * ⭐ ปุ่มออกจริง **ปิดไว้** เมื่อยังกรอกไม่ครบ พร้อมบอกว่าขาดอะไรและไปแก้ที่ไหน
 *    ไม่ใช่ปล่อยให้กดแล้วเด้ง toast แดง
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Download, CheckCircle2, XCircle, RefreshCw, Pencil, PenLine, Eye, ExternalLink } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadError } from '../../components/layout/LoadError';
import { Loading } from '../../components/layout/Loading';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import {
  useLetters, useIssueLetter, useCancelLetter, useRefreshLetter, useRecordPaperAck, fetchAckFileUrl,
  WAGE_UNIT_LABEL,
  WARNING_LEVEL_LABEL,
  type LetterRow, type LetterType, type LetterStatus,
} from '../../hooks';
import { apiDownload, apiFetchBlob, ApiError, uploadFile, resolveFileUrl } from '../../lib/api-client';

const TYPE_LABEL: Record<LetterType, string> = {
  employment_cert: 'รับรองการทำงาน',
  salary_cert: 'รับรองเงินเดือน',
  warning: 'หนังสือเตือน',
};

const STATUS: Record<LetterStatus, { label: string; cls: string }> = {
  draft: { label: 'ร่าง', cls: 'bg-slate-100 text-slate-700' },
  issued: { label: 'ออกแล้ว', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'ยกเลิก', cls: 'bg-gray-100 text-gray-500' },
};

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('th-TH', {
    timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: '2-digit',
  });
}

export function HRLetters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const empFilter = searchParams.get('emp')?.trim() ?? '';
  const [typeFilter, setTypeFilter] = useState<LetterType | ''>('');
  const [statusFilter, setStatusFilter] = useState<LetterStatus | ''>('');
  const { data, isLoading, error, refetch } = useLetters({
    ...(empFilter ? { emp: empFilter } : {}),
    ...(typeFilter ? { type: typeFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
  });
  const issue = useIssueLetter();
  const cancel = useCancelLetter();
  const refresh = useRefreshLetter();
  const toast = useToast();
  const consequence = useConsequence();
  const navigate = useNavigate();

  const [cancelTarget, setCancelTarget] = useState<LetterRow | null>(null);
  // ลงนามกระดาษ (fb#176) — ทางสำรองเมื่อพนักงานไม่เซ็นบนจอ
  const paperAck = useRecordPaperAck();
  const [paperTarget, setPaperTarget] = useState<LetterRow | null>(null);
  const [paperSignedOn, setPaperSignedOn] = useState('');
  const [paperFile, setPaperFile] = useState<File | null>(null);
  const todayIso = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10);

  async function openAckFile(id: string) {
    try {
      const url = resolveFileUrl(await fetchAckFileUrl(id));
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เปิดไฟล์ไม่สำเร็จ');
    }
  }

  async function submitPaperAck() {
    if (!paperTarget) return;
    if (!paperSignedOn) {
      toast.error('กรุณาระบุวันที่พนักงานลงนาม');
      return;
    }
    try {
      let filePath: string | undefined;
      if (paperFile) filePath = await uploadFile(paperFile, 'letter_signed', paperFile.name);
      await paperAck.mutateAsync({ id: paperTarget.id, signed_on: paperSignedOn, file_path: filePath });
      toast.success('บันทึกการลงนามบนกระดาษแล้ว');
      setPaperTarget(null);
      setPaperSignedOn('');
      setPaperFile(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }
  const [cancelReason, setCancelReason] = useState('');
  const [staleInfo, setStaleInfo] = useState<{
    row: LetterRow;
    changes: { label: string; before: string; after: string }[];
    /** ข้อความจริงจากเซิร์ฟเวอร์ — 409 มีหลายสาเหตุ ห้ามเดาแทนผู้ใช้ */
    message?: string;
  } | null>(null);

  async function handleIssue(row: LetterRow) {
    if (!row.view_hash) return;
    const wage = row.computed.certifiedWage;
    const warningLevel = row.input.warning?.level;
    const ok = await consequence({
      title: `ออก${TYPE_LABEL[row.letter_type]} — ${row.registry.empNameTh}`,
      intro: 'เมื่อกดยืนยัน ระบบจะดำเนินการต่อไปนี้',
      lines: [
        { label: 'วันที่บนเอกสาร', value: row.as_of_date ?? '—' },
        ...(row.letter_type === 'salary_cert' && wage
          ? [{
              label: 'ค่าจ้างที่รับรอง',
              value: `${wage.amount.toLocaleString('th-TH')} บาท/${WAGE_UNIT_LABEL[wage.unit]}`,
            }]
          : []),
        ...(row.letter_type === 'warning'
          ? [{
              label: 'ครั้งที่เตือน',
              value: warningLevel ? WARNING_LEVEL_LABEL[warningLevel] : '—',
            }]
          : []),
      ],
      effects: [
        'ออกเลขที่หนังสือและตรึงเนื้อหาถาวร แก้ไม่ได้อีก',
        'ถ้าออกผิดต้องยกเลิกแล้วออกฉบับใหม่',
        row.letter_type === 'warning'
          ? 'พนักงานจะเห็นหนังสือเตือนฉบับนี้ในเมนู "หนังสือของฉัน" และดาวน์โหลดได้'
          : 'พนักงานจะเห็นเอกสารฉบับนี้ในเมนู "หนังสือของฉัน" ของตัวเอง',
      ],
      danger: true,
      confirmText: 'ออกเอกสารจริง',
    });
    if (!ok) return;

    try {
      const res = await issue.mutateAsync({ id: row.id, expected_hash: row.view_hash });
      toast.success(`ออกเอกสารแล้ว เลขที่ ${res.doc_no}`);
    } catch (e) {
      // 409 พร้อมรายการที่เปลี่ยน = ข้อมูลทะเบียนขยับหลัง HR ตรวจ — ต้องให้เขาอ่านก่อน
      const changes = (e instanceof ApiError ? (e.details as { changes?: typeof staleInfo extends null ? never : { label: string; before: string; after: string }[] })?.changes : undefined);
      if (changes?.length) {
        setStaleInfo({ row, changes });
        return;
      }
      // 🚨 409 = "ร่างนี้ถูกแก้หลังจากคุณเปิดดู" คือ error ที่ต้อง**ไปทำอะไรก่อน**
      // เดิมแจ้งด้วย toast ที่หายเองใน 4 วินาที บนหน้าตารางยาว ๆ ผู้ใช้ที่จ้องปุ่มอยู่
      // ไม่ทันเห็น แล้วสรุปว่า "กดแล้วไม่มีอะไรเกิดขึ้น" — ต้องค้างเป็นกล่องพร้อมทางไปต่อ
      if (e instanceof ApiError && e.status === 409) {
        setStaleInfo({ row, changes: [], message: e.message });
        return;
      }
      toast.error(e instanceof Error ? e.message : 'ออกเอกสารไม่สำเร็จ');
    }
  }

  async function handleRefresh(row: LetterRow) {
    // ใช้แถวสดจากรายการที่เพิ่งโหลดใหม่ ไม่ใช่แถวที่จำไว้ตอนกดออกเอกสาร
    // (ตอน issue ล้ม รายการถูก invalidate ไปแล้ว revision ที่ค้างในกล่องจึงเก่า → 409 วนไม่จบ)
    const fresh = (data?.items ?? []).find((r) => r.id === row.id) ?? row;
    try {
      const res = await refresh.mutateAsync({ id: fresh.id, expected_revision: fresh.revision });
      setStaleInfo(null);
      if (res.changes?.length) {
        toast.success(`ดึงข้อมูลใหม่แล้ว — อัปเดต ${res.changes.length} รายการ กรุณาตรวจก่อนออก`);
      } else {
        toast.success('ดึงข้อมูลใหม่แล้ว — ไม่มีอะไรเปลี่ยน');
      }
    } catch (e) {
      // ชนกันอีก = มีคนอื่นแก้ร่างนี้พร้อมกัน โหลดรายการใหม่ให้เลย ผู้ใช้จะได้กดต่อได้
      await refetch();
      setStaleInfo(null);
      toast.error(
        e instanceof ApiError && e.status === 409
          ? 'ร่างนี้ถูกแก้โดยคนอื่นระหว่างนี้ — โหลดรายการใหม่ให้แล้ว กรุณาตรวจอีกครั้งก่อนออก'
          : e instanceof Error ? e.message : 'ดึงข้อมูลใหม่ไม่สำเร็จ',
      );
    }
  }

  function pdfPath(row: LetterRow): string {
    return `/hr/letters/${encodeURIComponent(row.id)}/pdf`;
  }
  function pdfFilename(row: LetterRow): string {
    return `${row.letter_type}-${(row.doc_no ?? 'draft').replace('/', '-')}.pdf`;
  }

  async function handleDownload(row: LetterRow) {
    try {
      await apiDownload(pdfPath(row), pdfFilename(row));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ');
    }
  }

  // fb#404 (TN 19 ต.ค. 2569) — "ดูร่าง" เปิดตรวจบนจอก่อน ไม่ต้องโหลดไฟล์ลงเครื่อง
  // (ร่างเปลี่ยนได้ทุกครั้งที่แก้/ดึงข้อมูลใหม่ — ไฟล์ที่โหลดไว้จะเก่าโดยไม่รู้ตัว)
  const [preview, setPreview] = useState<{ row: LetterRow; url: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);

  async function handlePreview(row: LetterRow) {
    setPreviewLoading(row.id);
    try {
      const blob = await apiFetchBlob(pdfPath(row));
      setPreview({ row, url: URL.createObjectURL(blob) });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เปิดร่างไม่สำเร็จ');
    } finally {
      setPreviewLoading(null);
    }
  }

  function closePreview() {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  function downloadPreview() {
    if (!preview) return;
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = pdfFilename(preview.row);
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async function submitCancel() {
    if (!cancelTarget) return;
    if (!cancelReason.trim()) {
      toast.error('กรุณาระบุเหตุผลที่ยกเลิก');
      return;
    }
    try {
      await cancel.mutateAsync({ id: cancelTarget.id, reason: cancelReason.trim() });
      toast.success('ยกเลิกหนังสือแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ยกเลิกไม่สำเร็จ');
    } finally {
      // ปิดกล่องและโหลดรายการใหม่เสมอ — ไม่งั้นกดยืนยันซ้ำแล้วได้ 404 เดิมวนไป
      setCancelTarget(null);
      setCancelReason('');
    }
  }

  const rows = (data?.items ?? []) as LetterRow[];
  const sel =
    'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400';
  const btn = 'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs disabled:opacity-50';

  return (
    <AdminLayout title="หนังสือ HR">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select className={sel} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as LetterType | '')}>
          <option value="">ทุกชนิด</option>
          {(Object.keys(TYPE_LABEL) as LetterType[]).map((t) => (
            <option key={t} value={t}>{TYPE_LABEL[t]}</option>
          ))}
        </select>
        <select
          className={sel}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as LetterStatus | '')}
        >
          <option value="">ทุกสถานะ</option>
          <option value="draft">ร่าง</option>
          <option value="issued">ออกแล้ว</option>
          <option value="cancelled">ยกเลิก</option>
        </select>
        {empFilter && (
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('emp');
              setSearchParams(next, { replace: true });
            }}
            className="inline-flex min-h-11 items-center rounded-full bg-brand-50 px-3 text-xs font-medium text-brand-700 hover:bg-brand-100"
            aria-label={`ล้างตัวกรองพนักงาน ${empFilter}`}
          >
            เฉพาะ: {empFilter} ✕
          </button>
        )}
        <button
          onClick={() => navigate('/hr/letters/new')}
          className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700"
        >
          <Plus className="w-4 h-4" /> ออกหนังสือใหม่
        </button>
      </div>

      {isLoading && <Loading />}
      {error && <LoadError error={error} onRetry={refetch} />}

      {!isLoading && !error && rows.length === 0 && (
        <EmptyState
          icon={FileText}
          title="ยังไม่มีหนังสือในระบบ"
          detail="ออกหนังสือรับรองการทำงาน หนังสือรับรองเงินเดือน หรือหนังสือเตือนได้จากปุ่มด้านบน"
        />
      )}

      {/* เดิมตัดที่ 300 แถวเงียบ ๆ — บริษัทที่ออกหนังสือมาหลายปีไม่มีทางรู้ว่าของเก่าหายจากจอ */}
      {!isLoading && !error && data?.has_more && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
          แสดง {data.limit} รายการล่าสุดเท่านั้น ยังมีที่เก่ากว่านี้อีก — ใช้ตัวกรองชนิดหรือสถานะด้านบนเพื่อดูรายการที่ต้องการ
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">เลขที่</th>
                <th className="text-left px-4 py-3 font-medium">ชนิด</th>
                <th className="text-left px-4 py-3 font-medium">พนักงาน</th>
                <th className="text-left px-4 py-3 font-medium">สถานะ</th>
                <th className="text-left px-4 py-3 font-medium">วันที่ออก</th>
                <th className="text-right px-4 py-3 font-medium">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const blocked = row.missing.length > 0;
                return (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 whitespace-nowrap font-medium">{row.doc_no ?? '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{TYPE_LABEL[row.letter_type]}</td>
                    <td className="px-4 py-3">{row.registry.empNameTh || row.emp_code}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUS[row.status].cls}`}>
                        {STATUS[row.status].label}
                      </span>
                      {row.status === 'cancelled' && row.cancel_reason && (
                        <div className="text-xs text-gray-500 mt-1">{row.cancel_reason}</div>
                      )}
                      {/* หลักฐานรับทราบหนังสือเตือน — เซ็นบนจอ / กระดาษ / ยังรอ */}
                      {row.ack && (
                        <div className="text-xs text-green-700 mt-1 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {row.ack.method === 'screen' ? 'เซ็นรับทราบบนจอ' : 'ลงนามกระดาษ'} {thaiDate(row.ack.method === 'paper' ? row.ack.signed_on : row.ack.acked_at)}
                          {row.ack.has_file && (
                            <button
                              type="button"
                              onClick={() => void openAckFile(row.id)}
                              className="underline ml-1 min-h-11"
                            >
                              เปิดสแกน
                            </button>
                          )}
                        </div>
                      )}
                      {row.needs_ack && (
                        <div className="text-xs text-amber-700 mt-1">รอพนักงานลงนามรับทราบ</div>
                      )}
                      {row.status === 'draft' && blocked && (
                        <div className="text-xs text-amber-700 mt-1">
                          ขาด: {row.missing.map((m) => m.label).join(' · ')}
                          {row.missing.some((m) => m.fixWhere === 'registry') && ' (แก้ที่ทะเบียนพนักงาน)'}
                          {/* หัวบริษัทกับผู้ลงนามอยู่คนละหน้ากับทะเบียนพนักงาน บอกผิดหน้า = ผู้ใช้หาไม่เจอ */}
                          {row.missing.some((m) => m.fixWhere === 'company') && ' (แก้ที่ตั้งค่าบริษัท)'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{thaiDate(row.issued_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {row.status === 'draft' ? (
                          <button
                            onClick={() => handlePreview(row)}
                            disabled={previewLoading === row.id}
                            title="เปิดดูร่างบนจอ ยังไม่ต้องโหลดไฟล์"
                            className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            {previewLoading === row.id ? 'กำลังเปิด…' : 'ดูร่าง'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDownload(row)}
                            className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            PDF
                          </button>
                        )}
                        {row.status === 'draft' && (
                          <>
                            <button
                              onClick={() => navigate(`/hr/letters/${row.id}/edit`)}
                              className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}
                            >
                              <Pencil className="w-3.5 h-3.5" /> แก้ร่าง
                            </button>
                            <button
                              onClick={() => handleRefresh(row)}
                              disabled={refresh.isPending}
                              title="ดึงตำแหน่ง วันเริ่มงาน และฐานค่าจ้างจากทะเบียนพนักงานมาใหม่"
                              className={`${btn} border border-gray-300 text-gray-700 hover:bg-gray-50`}
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> ดึงข้อมูลใหม่
                            </button>
                            <button
                              onClick={() => handleIssue(row)}
                              disabled={issue.isPending || blocked}
                              title={blocked ? 'ยังกรอกไม่ครบ' : 'ออกเอกสารจริง'}
                              className={`${btn} bg-green-600 text-white hover:bg-green-700`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> ออกจริง
                            </button>
                          </>
                        )}
                        {row.needs_ack && (
                          <button
                            onClick={() => { setPaperTarget(row); setPaperSignedOn(todayIso); setPaperFile(null); }}
                            title="พนักงานลงนามบนเอกสารฉบับกระดาษแล้ว — บันทึกเข้าระบบ (แนบสแกนได้)"
                            className={`${btn} min-h-11 border border-amber-300 text-amber-800 hover:bg-amber-50`}
                          >
                            <PenLine className="w-3.5 h-3.5" /> บันทึกลงนามกระดาษ
                          </button>
                        )}
                        {row.status !== 'cancelled' && (
                          <button
                            onClick={() => { setCancelTarget(row); setCancelReason(''); }}
                            className={`${btn} border border-red-300 text-red-700 hover:bg-red-50`}
                          >
                            <XCircle className="w-3.5 h-3.5" /> ยกเลิก
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ข้อมูลทะเบียนขยับหลัง HR ตรวจ — ให้อ่านก่อน แล้วค่อยดึงใหม่ */}
      <Modal
        open={staleInfo !== null}
        onClose={() => setStaleInfo(null)}
        title={staleInfo?.changes.length ? 'ข้อมูลในทะเบียนเปลี่ยนไปแล้ว' : 'ร่างนี้ถูกแก้ไขระหว่างทาง'}
      >
        {/* 409 มีหลายสาเหตุ — "ร่างถูกแก้ระหว่างทาง" กับ "ฉบับนี้ออกไปแล้ว" คนละเรื่องกัน
            เดาแทนผู้ใช้แล้วบอกให้กดดึงข้อมูลใหม่ทั้งที่ฉบับนั้นไม่ใช่ร่างแล้ว = พาไปผิดทาง */}
        <p className="text-sm text-gray-600 mb-3">
          {staleInfo?.changes.length
            ? 'ร่างนี้ตรวจไว้ก่อนที่ทะเบียนพนักงานจะถูกแก้ ระบบจึงยังไม่ออกเอกสารให้ กรุณาดูสิ่งที่เปลี่ยน แล้วกดดึงข้อมูลใหม่เพื่อตรวจอีกครั้ง'
            : (staleInfo?.message ??
               'ร่างนี้ถูกแก้ไขหลังจากที่คุณเปิดหน้านี้ ระบบจึงยังไม่ออกเอกสารให้')}
        </p>
        <div className="rounded-lg border border-gray-200 divide-y text-sm">
          {staleInfo?.changes.map((c) => (
            <div key={c.label} className="flex gap-2 px-3 py-2">
              <div className="w-40 shrink-0 text-gray-500">{c.label}</div>
              <div className="flex-1">
                <span className="text-gray-500 line-through">{c.before}</span>
                <span className="mx-1.5">→</span>
                <span className="font-medium">{c.after}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => { setStaleInfo(null); void refetch(); }}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            ปิดแล้วโหลดรายการใหม่
          </button>
          <button
            onClick={() => staleInfo && handleRefresh(staleInfo.row)}
            disabled={refresh.isPending}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {refresh.isPending ? 'กำลังดึง…' : 'ดึงข้อมูลใหม่'}
          </button>
        </div>
      </Modal>

      <Modal
        open={paperTarget !== null}
        onClose={() => setPaperTarget(null)}
        title={`บันทึกการลงนามบนกระดาษ ${paperTarget?.doc_no ?? ''}`}
      >
        <p className="text-sm text-gray-600 mb-3">
          ใช้เมื่อพนักงานลงนามรับทราบบนเอกสารฉบับพิมพ์แล้ว ระบบจะบันทึกเป็นหลักฐาน (แก้หรือลบไม่ได้) และ PDF จะระบุว่าลงนามบนกระดาษ
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1">วันที่พนักงานลงนาม</label>
        <input
          type="date"
          className="input w-full border border-gray-300 rounded-lg px-3 py-2 text-base"
          value={paperSignedOn}
          max={todayIso}
          onChange={(e) => setPaperSignedOn(e.target.value)}
        />
        <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">สแกน/รูปถ่ายฉบับที่ลงนาม (ไม่บังคับ · PDF/JPG/PNG ≤ 5 MB)</label>
        <input
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          className="block w-full text-sm"
          onChange={(e) => setPaperFile(e.target.files?.[0] ?? null)}
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => setPaperTarget(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 min-h-11">
            ปิด
          </button>
          <button
            onClick={() => void submitPaperAck()}
            disabled={paperAck.isPending}
            className="px-4 py-2 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50 min-h-11"
          >
            {paperAck.isPending ? 'กำลังบันทึก…' : 'บันทึกหลักฐาน'}
          </button>
        </div>
      </Modal>

      <Modal
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title={`ยกเลิกหนังสือ ${cancelTarget?.doc_no ?? '(ร่าง)'}`}
      >
        <p className="text-sm text-gray-600 mb-3">
          หนังสือที่ยกเลิกจะยังอยู่ในระบบเพื่อเป็นร่องรอย และเวลาดาวน์โหลดจะมีคำว่า “ยกเลิก” ประทับบนเอกสาร
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1">เหตุผลที่ยกเลิก</label>
        <textarea
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-brand-400"
          rows={3}
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="เช่น ออกผิดคน / ระบุตำแหน่งผิด"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => setCancelTarget(null)}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            ปิด
          </button>
          <button
            onClick={submitCancel}
            disabled={cancel.isPending}
            className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {cancel.isPending ? 'กำลังยกเลิก…' : 'ยืนยันยกเลิก'}
          </button>
        </div>
      </Modal>

      {/* fb#404 — ดูร่างบนจอ: ตรวจก่อนออกจริงโดยไม่ต้องโหลดไฟล์ · มือถือเปิดแท็บใหม่แทน iframe ได้ */}
      <Modal
        open={preview !== null}
        onClose={closePreview}
        title={preview ? `ร่าง${TYPE_LABEL[preview.row.letter_type]} — ${preview.row.registry.empNameTh || preview.row.emp_code}` : 'ร่าง'}
        size="max-w-4xl"
      >
        {preview && (
          <>
            <iframe
              src={preview.url}
              title="ร่างเอกสาร"
              className="w-full h-[70vh] border border-gray-200 rounded-lg bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-2">
              นี่คือร่าง ยังไม่มีเลขที่ · ถ้าตัวอักษรเพี้ยนหรือมองไม่เห็นบนมือถือ กด “เปิดแท็บใหม่”
            </p>
            <div className="flex justify-end gap-2 mt-3 flex-wrap">
              <button
                onClick={() => window.open(preview.url, '_blank', 'noopener')}
                className="min-h-11 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5"
              >
                <ExternalLink className="w-4 h-4" /> เปิดแท็บใหม่
              </button>
              <button
                onClick={downloadPreview}
                className="min-h-11 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5"
              >
                <Download className="w-4 h-4" /> ดาวน์โหลด
              </button>
              <button
                onClick={closePreview}
                className="min-h-11 px-4 py-2 rounded-lg bg-gray-900 text-white font-medium hover:bg-gray-800"
              >
                ปิด
              </button>
            </div>
          </>
        )}
      </Modal>
    </AdminLayout>
  );
}
